import type { ViewerShell } from '../../shell';
import type { SeriesRef } from '../../dicomweb';
import { isImageSeries } from '../../study';
import { openDialog, paragraph } from '../../ui/dialogs';
import { RisError } from '../client';
import { type AnalysisStreamEnd, type AssistantApi, type AssistantFinding } from './api';
import { conversationStudy } from './context';
import { aiLimitText, streamErrorText } from './errors';
import { setFindings } from './findings-overlay';
import type { AssistantPanelHook } from './panel';
import { type AnalysisJob, type JobDepth, type JobProgress, reviewsDirectly } from './shapes';

/**
 * "Revisar serie" on a whole series (contract v2 §3): ris runs a large-study job and pushes its
 * progress as SSE, read with fetch and the ticket header (never EventSource, never polling). The card
 * shows a progress bar, the findings as they come, then the stored set with their slice ranges.
 */

export const ANALYSIS_TEXT = {
  depthTitle: 'Revisar la serie completa',
  depthIntro: 'Elija la profundidad de la revisión. La exhaustiva tarda más y usa más ventanas en TC.',
  screen: 'Rápida',
  thorough: 'Exhaustiva',
  cancel: 'Cancelar',
  cardTitle: 'Revisión de la serie',
  queued: 'En cola…',
  running: 'Revisando la serie…',
  done: (n: number) => (n === 0 ? 'Revisión terminada: sin hallazgos sugeridos.' : n === 1 ? 'Revisión terminada: 1 hallazgo sugerido.' : `Revisión terminada: ${n} hallazgos sugeridos.`),
  cached: 'Resultado de una revisión idéntica de las últimas 24 h.',
  failed: 'La revisión de la serie falló.',
  cancelled: 'Revisión cancelada.',
  lost: 'Se perdió la conexión con la revisión; sigue en curso en el servidor.',
  resume: 'Reanudar',
  cancelJob: 'Cancelar revisión',
  cancelFailed: 'No fue posible cancelar la revisión.',
  noSeries: 'No hay una serie de imágenes del estudio en el visor.',
  startFailed: 'No fue posible iniciar la revisión de la serie.',
  progressLabel: 'Progreso de la revisión',
  alreadyRunning: 'Ya hay una revisión de esta serie en curso; se muestra abajo.',
} as const;

/** What ris's stage names read as; an unknown stage shows no label. */
const STAGE_LABELS: Record<string, string> = {
  geometry: 'leyendo la serie',
  mosaic: 'preparando imágenes',
  render: 'preparando imágenes',
  screen: 'cribado',
  merge: 'uniendo resultados',
  confirm: 'confirmando hallazgos',
  store: 'guardando',
};

const NO_JOBS_STATUSES = new Set([404, 405]);

export interface AnalysisContext {
  shell: ViewerShell;
  api: AssistantApi;
  panel: AssistantPanelHook;
  renderFinding(finding: AssistantFinding): HTMLElement;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** In-viewer chooser (never window.confirm): the depth, or null when cancelled. */
export function chooseDepth(shell: ViewerShell): Promise<JobDepth | null> {
  return new Promise((resolve) => {
    let answered = false;
    const pick = (value: JobDepth | null, close: () => void) => {
      answered = true;
      close();
      resolve(value);
    };
    const { body, dialog } = openDialog(shell, {
      title: ANALYSIS_TEXT.depthTitle,
      className: 'assistant-depth-dialog',
      buttons: [
        { label: ANALYSIS_TEXT.cancel, onClick: (close) => pick(null, close) },
        { label: ANALYSIS_TEXT.thorough, onClick: (close) => pick('thorough', close) },
        { label: ANALYSIS_TEXT.screen, primary: true, onClick: (close) => pick('screen', close) },
      ],
    });
    paragraph(body, ANALYSIS_TEXT.depthIntro, 'message-text');
    const observer = new MutationObserver(() => {
      if (dialog.isConnected) return;
      observer.disconnect();
      if (!answered) resolve(null);
    });
    observer.observe(shell.regions.overlayLayer, { childList: true });
  });
}

/** The image series to review: the active viewport's when it shows the conversation's study, else its first. */
export function targetSeries(shell: ViewerShell): SeriesRef | null {
  const study = conversationStudy(shell);
  const shown = shell.activeViewport()?.currentStack?.series;
  if (shown && shown.studyInstanceUID === study && isImageSeries(shown)) return shown;
  const ref = shell.studies().find((s) => s.studyInstanceUID === study);
  return ref?.series.find(isImageSeries) ?? null;
}

/** One analysis card: progress, live findings, final findings, cancel / resume. */
class AnalysisCard {
  readonly root = el('div', 'assistant-analysis');
  private readonly bar = el('progress', 'assistant-analysis-bar');
  private readonly status = el('div', 'assistant-analysis-status');
  private readonly findings = el('div', 'assistant-findings');
  private readonly controls = el('div', 'assistant-analysis-controls');
  private abort: AbortController | null = null;
  private finished = false;

  /** Queued, running or lost: the job may still be spending model calls on the server. */
  get active(): boolean {
    return !this.finished;
  }

  constructor(private readonly ctx: AnalysisContext, private readonly jobId: string, title: string) {
    this.root.dataset.jobId = jobId;
    this.bar.setAttribute('aria-label', ANALYSIS_TEXT.progressLabel);
    this.bar.max = 1;
    this.bar.value = 0;
    this.status.setAttribute('role', 'status');
    this.findings.setAttribute('role', 'list');
    this.root.append(el('div', 'assistant-analysis-title', title), this.bar, this.status, this.findings, this.controls);
    this.setState('queued', ANALYSIS_TEXT.queued);
  }

  private setState(state: string, text: string): void {
    this.status.textContent = text;
    // Progress events repeat the state: rebuilding the buttons each time would swallow a click on them.
    if (this.root.dataset.state === state) return;
    this.root.dataset.state = state;
    this.controls.replaceChildren();
    if (state === 'queued' || state === 'running' || state === 'lost') this.controls.appendChild(this.button(ANALYSIS_TEXT.cancelJob, 'assistant-analysis-cancel', () => void this.cancel()));
    if (state === 'lost') this.controls.appendChild(this.button(ANALYSIS_TEXT.resume, 'assistant-analysis-resume', () => void this.follow()));
  }

  private button(label: string, className: string, onClick: () => void): HTMLButtonElement {
    const b = el('button', `assistant-btn ${className}`, label);
    b.type = 'button';
    b.addEventListener('click', onClick);
    return b;
  }

  private progress(p: JobProgress): void {
    if (this.finished) return;
    this.bar.max = Math.max(1, p.total);
    this.bar.value = Math.min(p.done, this.bar.max);
    const stage = p.stage ? STAGE_LABELS[p.stage] : undefined;
    const count = p.total > 0 ? ` ${p.done} de ${p.total}` : '';
    this.setState('running', `${ANALYSIS_TEXT.running}${count}${stage ? ` · ${stage}` : ''}`);
  }

  private liveFinding(finding: AssistantFinding): void {
    if (this.finished || this.findings.querySelector(`[data-finding-id="${CSS.escape(finding.id)}"]`)) return;
    this.findings.appendChild(this.ctx.renderFinding(finding));
  }

  /** Follows the job's events; a closed stream asks the job once for where it stands. */
  async follow(): Promise<void> {
    this.abort?.abort();
    const abort = new AbortController();
    this.abort = abort;
    this.setState('running', ANALYSIS_TEXT.running);
    let end: AnalysisStreamEnd;
    try {
      end = await this.ctx.api.analysisEvents(this.jobId, { onProgress: (p) => this.progress(p), onFinding: (f) => this.liveFinding(f) }, abort.signal);
    } catch (error) {
      if (abort.signal.aborted) return;
      console.warn('[viewer] analysis stream failed', error);
      end = { kind: 'closed' };
    }
    if (this.abort !== abort || this.finished) return;
    if (end.kind === 'error') return this.fail(end.detail, end.status);
    await this.refresh(end.kind === 'done');
  }

  /** Reads the stored job (the authoritative findings). `expectDone`: the stream said done. */
  async refresh(expectDone = false, cached = false): Promise<void> {
    let job: AnalysisJob;
    try {
      job = await this.ctx.api.analysis(this.jobId);
    } catch (error) {
      console.warn('[viewer] analysis read failed', error);
      return expectDone ? this.fail('') : this.setState('lost', ANALYSIS_TEXT.lost);
    }
    this.show(job, cached);
  }

  show(job: AnalysisJob, cached = false): void {
    if (job.status === 'done') return this.complete(job, cached);
    if (job.status === 'failed') return this.fail(job.error ?? '');
    if (job.status === 'cancelled') return this.end('cancelled', ANALYSIS_TEXT.cancelled);
    this.progress(job.progress);
    this.setState('lost', ANALYSIS_TEXT.lost);
  }

  private complete(job: AnalysisJob, cached: boolean): void {
    this.bar.max = Math.max(1, job.progress.total);
    this.bar.value = this.bar.max;
    this.findings.replaceChildren(...job.findings.map((f) => this.ctx.renderFinding(f)));
    if (job.findings.length > 0) setFindings(job.findings);
    this.end('done', ANALYSIS_TEXT.done(job.findings.length));
    // What was actually analysed, so "sin hallazgos" never hides slices that were skipped.
    if (job.summary) this.status.appendChild(el('div', 'assistant-trace assistant-analysis-summary', job.summary));
    if (cached) this.status.appendChild(el('div', 'assistant-trace', ANALYSIS_TEXT.cached));
  }

  private fail(detail: string, status: number | null = null): void {
    this.end('failed', streamErrorText(status, detail) ?? ANALYSIS_TEXT.failed);
  }

  private end(state: string, text: string): void {
    this.finished = true;
    this.abort?.abort();
    this.abort = null;
    this.setState(state, text);
  }

  private async cancel(): Promise<void> {
    try {
      await this.ctx.api.cancelAnalysis(this.jobId);
      this.end('cancelled', ANALYSIS_TEXT.cancelled);
    } catch (error) {
      console.warn('[viewer] analysis cancel failed', error);
      this.ctx.shell.toast(ANALYSIS_TEXT.cancelFailed, 'error');
    }
  }
}

function cardTitle(series: SeriesRef, depth: JobDepth): string {
  const parts: string[] = [ANALYSIS_TEXT.cardTitle];
  if (series.description) parts.push(series.description);
  parts.push(depth === 'thorough' ? ANALYSIS_TEXT.thorough : ANALYSIS_TEXT.screen);
  return parts.join(' · ');
}

/**
 * Cards of jobs this page started, by study + series + depth. ris only reuses a DONE identical job, so
 * a second click while one runs would pay for the same series twice: the viewer shows the running one.
 */
const activeCards = new Map<string, AnalysisCard>();

/**
 * The "Revisar serie" chip. Answers false when ris has no analyses route (an older deploy) or the series
 * is an X-ray's one or two images (``reviewsDirectly``): the chip then asks the plain sweep question.
 */
export async function runSeriesAnalysis(ctx: AnalysisContext): Promise<boolean> {
  const series = targetSeries(ctx.shell);
  if (!series) {
    ctx.shell.toast(ANALYSIS_TEXT.noSeries, 'warning');
    return true;
  }
  if (reviewsDirectly(series.instanceCount)) return false;
  const depth = await chooseDepth(ctx.shell);
  if (!depth) return true;
  const study = conversationStudy(ctx.shell);
  const key = [study, series.seriesInstanceUID, depth].join('|');
  const running = activeCards.get(key);
  if (running?.active) {
    ctx.shell.toast(ANALYSIS_TEXT.alreadyRunning, 'warning');
    running.root.scrollIntoView?.({ block: 'nearest' });
    return true;
  }
  let start;
  try {
    start = await ctx.api.startAnalysis(study, series.seriesInstanceUID, depth);
  } catch (error) {
    if (error instanceof RisError && NO_JOBS_STATUSES.has(error.status)) return false;
    console.warn('[viewer] analysis start failed', error);
    ctx.shell.toast(aiLimitText(error) ?? ANALYSIS_TEXT.startFailed, 'error');
    return true;
  }
  const card = new AnalysisCard(ctx, start.jobId, cardTitle(series, depth));
  activeCards.set(key, card);
  ctx.panel.appendResult(card.root);
  if (start.cached) await card.refresh(true, true);
  else await card.follow();
  return true;
}
